import {Octicon, Box, Text, Link} from '@primer/react'
import {useFragment, graphql} from 'react-relay'

import {getSourceIcon} from '../utils/get-source-icon'
import {issueHovercardPath, issueLinkedPullRequestHovercardPath} from '@github-ui/paths'
import {LockIcon} from '@primer/octicons-react'
import {LABELS} from '..'
import type {CrossReferencedEventInner$key} from './__generated__/CrossReferencedEventInner.graphql'

type CrossReferencedEventInnerProps = {
  queryRef: CrossReferencedEventInner$key
  taregtRepositoryId: string | undefined
}

export function CrossReferencedEventInner({queryRef, taregtRepositoryId}: CrossReferencedEventInnerProps): JSX.Element {
  const source = useFragment(
    graphql`
      fragment CrossReferencedEventInner on ReferencedSubject {
        __typename
        ... on Issue {
          id
          title
          url
          number
          stateReason
          repository {
            id
            name
            isPrivate
            owner {
              login
            }
          }
        }
        ... on PullRequest {
          id
          title
          url
          number
          state
          isDraft
          isInMergeQueue
          repository {
            id
            name
            isPrivate
            owner {
              login
            }
          }
        }
      }
    `,
    queryRef,
  )
  const isIssue = source.__typename === 'Issue'
  const isPullRequest = source.__typename === 'PullRequest'

  if (!isIssue && !isPullRequest) {
    return <></>
  }

  const state = isPullRequest ? source.state : undefined
  const stateReason = isIssue ? source.stateReason : undefined
  const isDraft = isPullRequest ? source.isDraft : undefined
  const isInMergeQueue = isPullRequest ? source.isInMergeQueue : undefined
  const icon = getSourceIcon(source.__typename, state, stateReason, isDraft, isInMergeQueue)

  const dataHovercardUrl =
    source.repository && source.number
      ? isIssue
        ? issueHovercardPath({
            repo: source.repository.name,
            owner: source.repository.owner.login,
            issueNumber: source.number,
          })
        : issueLinkedPullRequestHovercardPath({
            owner: source.repository.owner.login,
            repo: source.repository.name,
            pullRequestNumber: source.number,
          })
      : null

  const repoIssueText =
    source.repository.id === taregtRepositoryId
      ? `#${source.number}`
      : `${source.repository.owner.login}/${source.repository.name}#${source.number}`

  const privateDescriptionId = `${source.id}-private-description`
  return (
    <Box
      as="li"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Octicon icon={icon.icon} size={16} sx={{color: icon.color}} />
      <Link
        sx={{color: 'fg.default', minWidth: 0}}
        href={source.url}
        data-hovercard-url={dataHovercardUrl}
        aria-describedby={source.repository.isPrivate ? privateDescriptionId : undefined}
      >
        {source.title} <Text sx={{color: 'fg.muted'}}>{repoIssueText}</Text>
      </Link>
      {source.repository.isPrivate && (
        <>
          <Octicon icon={LockIcon} />
          <Text id={privateDescriptionId} className="sr-only">
            {LABELS.crossReferencedEvent.privateEventDescription}
          </Text>
        </>
      )}
    </Box>
  )
}

try{ CrossReferencedEventInner.displayName ||= 'CrossReferencedEventInner' } catch {}